import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { auth, provider as googleProvider } from '../components/FirebaseConfig';
import api from '../services/api';

const AuthContext = createContext(null);

const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true';

const getDevUser = () => ({
  uid: 'dev-user-00000',
  email: 'dev@garaoke.com',
  displayName: 'Dev User',
  photoURL: null,
  role: 'admin',
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cargar estado inicial y sincronizar con backend
  useEffect(() => {
    if (DEV_MODE) {
      const savedDevAuth = localStorage.getItem('garaoke-dev-auth');

      if (savedDevAuth === 'true') {
        const devUser = getDevUser();
        setUser(devUser);
        setProfile(devUser);
        setSubscription({ plan: 'premium', status: 'active' });
        localStorage.setItem('garaoke-auth-token', 'dev-token');
        if (typeof window !== 'undefined') {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'login_success',
            user_id: devUser.uid,
            user_plan: 'premium',
          });
        }
      } else {
        setUser(null);
        setProfile(null);
        setSubscription(null);
      }
      setLoading(false);
      return;
    }

    // Limpiar dev auth si ya no estamos en DEV_MODE
    localStorage.removeItem('garaoke-dev-auth');

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdToken();
          localStorage.setItem('garaoke-auth-token', token);

          // Obtener suscripción y perfil REAL desde el backend / Firestore
          const verifyRes = await api.verifyAuth();
          const serverData = verifyRes.data;

          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
          });
          setProfile(serverData.user || {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
          });
          const userSub = serverData.subscription || { plan: 'free', status: 'active' };
          setSubscription(userSub);

          // Rastrear login de usuario y plan en Google Tag Manager (GTM)
          if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'login_success',
              user_id: firebaseUser.uid,
              user_plan: userSub.plan || 'free',
            });
          }
        } catch (err) {
          console.error('Error sincronizando sesión con backend:', err);
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
          });
          setSubscription({ plan: 'free', status: 'active' });

          if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'login_success',
              user_id: firebaseUser.uid,
              user_plan: 'free',
            });
          }
        }
      } else {
        setUser(null);
        setProfile(null);
        setSubscription(null);
        localStorage.removeItem('garaoke-auth-token');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /**
   * Registro con Email y Contraseña
   */
  const signUp = useCallback(async (email, password, displayName) => {
    if (DEV_MODE) {
      const devUser = { ...getDevUser(), email, displayName: displayName || 'Dev User' };
      localStorage.setItem('garaoke-dev-auth', 'true');
      localStorage.setItem('garaoke-auth-token', 'dev-token');
      setUser(devUser);
      setProfile(devUser);
      setSubscription({ plan: 'premium', status: 'active' });
      return { user: devUser, error: null };
    }

    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }
      const token = await result.user.getIdToken();
      localStorage.setItem('garaoke-auth-token', token);

      // Verificar y sincronizar con backend
      try {
        const verifyRes = await api.verifyAuth();
        setSubscription(verifyRes.data?.subscription || { plan: 'free', status: 'active' });
      } catch (syncErr) {
        console.warn('Sync error on signup:', syncErr.message);
      }

      setUser(result.user);
      return { user: result.user, error: null };
    } catch (err) {
      console.error('Sign-Up error:', err);
      return { user: null, error: err.message };
    }
  }, []);

  /**
   * Login con Email y Contraseña
   */
  const signIn = useCallback(async (email, password) => {
    if (DEV_MODE) {
      const devUser = getDevUser();
      localStorage.setItem('garaoke-dev-auth', 'true');
      localStorage.setItem('garaoke-auth-token', 'dev-token');
      setUser(devUser);
      setProfile(devUser);
      setSubscription({ plan: 'premium', status: 'active' });
      return { user: devUser, error: null };
    }

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const token = await result.user.getIdToken();
      localStorage.setItem('garaoke-auth-token', token);

      try {
        const verifyRes = await api.verifyAuth();
        setSubscription(verifyRes.data?.subscription || { plan: 'free', status: 'active' });
        setProfile(verifyRes.data?.user || result.user);
      } catch (syncErr) {
        console.warn('Sync error on signin:', syncErr.message);
      }

      setUser(result.user);
      return { user: result.user, error: null };
    } catch (err) {
      console.error('Sign-In error:', err);
      return { user: null, error: err.message };
    }
  }, []);

  /**
   * Google Sign-In
   */
  const signInWithGoogle = useCallback(async () => {
    if (DEV_MODE) {
      const devUser = getDevUser();
      localStorage.setItem('garaoke-dev-auth', 'true');
      localStorage.setItem('garaoke-auth-token', 'dev-token');
      setUser(devUser);
      setProfile(devUser);
      setSubscription({ plan: 'premium', status: 'active' });
      return { user: devUser, error: null };
    }

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const token = await result.user.getIdToken();
      localStorage.setItem('garaoke-auth-token', token);

      try {
        const verifyRes = await api.verifyAuth();
        setSubscription(verifyRes.data?.subscription || { plan: 'free', status: 'active' });
        setProfile(verifyRes.data?.user || result.user);
      } catch (syncErr) {
        console.warn('Sync error on Google signin:', syncErr.message);
      }

      setUser(result.user);
      return { user: result.user, error: null };
    } catch (err) {
      console.error('Google Sign-In error:', err);
      return { user: null, error: err.message };
    }
  }, []);

  /**
   * Activar plan (sincroniza en el backend o en dev)
   */
  const selectPlan = useCallback(async (planName) => {
    if (DEV_MODE) {
      setSubscription({ plan: planName, status: 'active' });
      return;
    }

    try {
      // Activar a través de la API para que quede persistido en Firestore
      if (planName === 'free') {
        await api.activateFreePlan();
      }
      const verifyRes = await api.verifyAuth();
      if (verifyRes.data?.subscription) {
        setSubscription(verifyRes.data.subscription);
      }
    } catch (err) {
      console.error('Error selecting plan:', err);
    }
  }, []);

  /**
   * Sign Out
   */
  const signOutUser = useCallback(async () => {
    localStorage.removeItem('garaoke-dev-auth');
    localStorage.removeItem('garaoke-auth-token');

    if (!DEV_MODE) {
      try {
        await firebaseSignOut(auth);
      } catch (e) {
        console.warn('Sign out error', e);
      }
    }

    setUser(null);
    setProfile(null);
    setSubscription(null);
  }, []);

  /**
   * Helpers de suscripción
   */
  const hasActivePlan = useCallback(() => {
    return !!subscription && subscription.status === 'active';
  }, [subscription]);

  const currentPlan = useCallback(() => {
    return subscription?.plan || null;
  }, [subscription]);

  const value = {
    user,
    profile,
    subscription,
    loading,
    DEV_MODE,
    signUp,
    signIn,
    signInWithGoogle,
    signOut: signOutUser,
    selectPlan,
    hasActivePlan,
    currentPlan,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};

export default AuthContext;
